import { Style } from 'inlines'
import { useClient, useQuery } from '@based/sdk/react'
import { Title } from '@app/components/core/typography/Title'
import { Text } from '@app/components/core/typography/Text'
import { Button } from '@app/components/core/button/Button'
import { faPlus, faCheck } from '@fortawesome/pro-regular-svg-icons'
import { Card } from './Card'
import { AvatarStack } from '../avatarStack/AvatarStack'
import Column from '@app/components/layout/Column'
import { Author } from '../author/Author'
import { useState } from 'react'
import Row from '@app/components/layout/Row'
import { useUserContext } from '@app/util/hooks/useUserContext'
import { useTranslations } from '@i18n/useLanguage'
import { ViewHousingFormModel } from 'functions/app/housing-form/view'
import useMediaQuery from '@app/util/useMediaQuery'
import { useAuthModal } from '@app/util/hooks/useAuthModal'
import { SocialShareModal } from '../modal/SocialShareModal'
import { Tooltip } from '@app/components/core/tooltip/Tooltip'

export interface HousingFormPageHeaderCardProps {
  housingForm: ViewHousingFormModel
  style?: Style
}

export const HousingFormPageHeaderCard = ({
  housingForm,
  style,
}: HousingFormPageHeaderCardProps) => {
  const client = useClient()
  const { user } = useUserContext()
  const t = useTranslations().app
  const isMobile = useMediaQuery()
  const { withAuthCheck } = useAuthModal()

  const followerCountQuery = useQuery('count-followers', {
    entityType: 'housingForm',
    entityIds: [housingForm.id],
  })

  const [shareHousingFormModalOpen, setShareHousingFormModalOpen] =
    useState(false)
  const isFollowingQuery = useQuery(user?.id ? 'is-following' : null, {
    entityType: 'housingForm',
    entityId: housingForm.id,
    userId: user?.id,
  })
  const isFollowing = (isFollowingQuery.data as boolean) ?? false

  const toggleFollow = () => {
    void withAuthCheck(async () => {
      await client.call('follow-housing-form', {
        housingFormId: housingForm.id,
      })
    })
  }

  return (
    <Card
      image={housingForm?.images?.[0]?.src}
      imageStyle={{
        height: '18rem',
      }}
      padded={false}
      border={true}
      style={{
        width: '100%',
        ...style,
      }}
    >
      <Column
        gap="1rem"
        style={{
          flex: '1 0 0',
          width: '100%',
        }}
      >
        <Column
          gap="0.5rem"
          style={{
            width: '100%',
          }}
        >
          <Row
            style={{ width: '100%', flexWrap: isMobile ? 'wrap' : 'nowrap' }}
          >
            <Title.H1 style={{ margin: 0, fontSize: '3rem' }}>
              {housingForm.title}
            </Title.H1>
            <Row style={{ marginLeft: isMobile ? '0' : 'auto' }}>
              <Button
                variant="secondary"
                label={t.common.share}
                onClick={() => setShareHousingFormModalOpen(true)}
              />
              <Tooltip content={t.common.followTooltip}>
                <Button
                  variant="primary"
                  label={
                    isFollowing
                      ? t.housingFormPage.following
                      : t.housingFormPage.follow
                  }
                  icon={isFollowing ? faCheck : faPlus}
                  iconPosition="left"
                  onClick={toggleFollow}
                />
              </Tooltip>
            </Row>
          </Row>

          {housingForm?.ambassador && (
            <Row gap="0.5rem" style={{ alignItems: 'center' }}>
              <Text.Medium>{t.housingFormPage.ambassador}</Text.Medium>
              <Author
                avatarSize="extra-extra-small"
                name={`${housingForm.ambassador.firstName} ${housingForm.ambassador.lastName}`}
                avatarSrc={housingForm.ambassador.avatar}
              />
            </Row>
          )}
        </Column>

        {/* Followers */}
        {housingForm?.followers?.length > 0 && (
          <Row gap="0.5rem" style={{ alignItems: 'center' }}>
            <AvatarStack
              size="extra-small"
              avatars={housingForm.followers}
              total={followerCountQuery.data?.[0] ?? 0}
              version="dark"
            />

            <Text.Small
              style={{
                color: 'var(--shades-blue-500)',
              }}
            >
              {t.common.followers}
            </Text.Small>
          </Row>
        )}
      </Column>

      <SocialShareModal
        isOpen={shareHousingFormModalOpen}
        onClose={() => setShareHousingFormModalOpen(false)}
        title={housingForm?.title}
      />
    </Card>
  )
}
